/**
 * JsSIP-based WebPhone client.
 *
 * Encapsulates SIP registration, outbound calls, recording, and the
 * combined-audio stream that the live-listen feature shares to admins.
 *
 * The class is a singleton (one UA per browser tab) — instantiate it
 * through `getWebPhone(cfg)` from the WebPhoneContext.
 */
import JsSIP from 'jssip';

const log = (...args) => console.log('[WebPhone]', ...args);

class WebPhone {
  constructor() {
    this.ua = null;
    this.session = null;
    this.config = null;
    this.localStream = null;
    this.remoteStream = null;
    this.combinedStream = null;        // mic + remote, used for recording & broadcasting
    this.audioContext = null;
    this.audioMixDestination = null;
    this.remoteAudioEl = null;
    this.whisperAudioEl = null;        // plays admin's whisper audio (agent side)
    this.mediaRecorder = null;
    this.recordedChunks = [];
    this.listeners = new Map();
    this.callMeta = null;              // { id, lead_id, phone_e164, lead_name }
  }

  on(event, cb) {
    if (!this.listeners.has(event)) this.listeners.set(event, new Set());
    this.listeners.get(event).add(cb);
    return () => this.listeners.get(event)?.delete(cb);
  }
  emit(event, payload) {
    this.listeners.get(event)?.forEach((cb) => { try { cb(payload); } catch (e) { console.error(e); } });
  }

  isRegistered() { return !!(this.ua && this.ua.isRegistered()); }
  inCall() { return !!this.session; }

  async register(cfg) {
    // cfg = { wsUri, sipUri, password, displayName }
    if (this.ua) {
      try { this.ua.stop(); } catch { /* noop */ }
      this.ua = null;
    }
    this.config = cfg;
    const socket = new JsSIP.WebSocketInterface(cfg.wsUri);
    this.ua = new JsSIP.UA({
      sockets: [socket],
      uri: cfg.sipUri,
      password: cfg.password,
      display_name: cfg.displayName || cfg.sipUri,
      register: true,
      session_timers: false,
    });
    this.ua.on('registered', () => { log('registered'); this.emit('registered'); });
    this.ua.on('unregistered', () => { log('unregistered'); this.emit('unregistered'); });
    this.ua.on('registrationFailed', (e) => { log('regFailed', e); this.emit('registrationFailed', e); });
    this.ua.on('connecting', () => this.emit('connecting'));
    this.ua.on('connected', () => this.emit('connected'));
    this.ua.on('disconnected', () => this.emit('disconnected'));
    this.ua.start();
  }

  unregister() {
    try { this.ua && this.ua.stop(); } catch { /* noop */ }
    this.ua = null;
  }

  /** Ensure the hidden <audio> element used to play remote SIP audio exists. */
  _ensureRemoteAudioEl() {
    if (this.remoteAudioEl) return;
    const el = document.createElement('audio');
    el.id = 'webphone-remote-audio';
    el.autoplay = true;
    el.style.display = 'none';
    document.body.appendChild(el);
    this.remoteAudioEl = el;
  }
  _ensureWhisperAudioEl() {
    if (this.whisperAudioEl) return;
    const el = document.createElement('audio');
    el.id = 'webphone-whisper-audio';
    el.autoplay = true;
    el.style.display = 'none';
    document.body.appendChild(el);
    this.whisperAudioEl = el;
  }

  /** Build a single MediaStream that contains BOTH the local mic and the remote audio. */
  _buildCombinedStream() {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      this.audioContext = new Ctx();
      this.audioMixDestination = this.audioContext.createMediaStreamDestination();
      const srcs = [];
      if (this.localStream) srcs.push(this.audioContext.createMediaStreamSource(this.localStream));
      if (this.remoteStream) srcs.push(this.audioContext.createMediaStreamSource(this.remoteStream));
      srcs.forEach((s) => s.connect(this.audioMixDestination));
      this.combinedStream = this.audioMixDestination.stream;
    } catch (e) {
      log('combined stream build failed', e);
    }
  }

  _startRecording() {
    if (!this.combinedStream) return;
    try {
      const mimeCandidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus'];
      const mimeType = mimeCandidates.find((m) => window.MediaRecorder?.isTypeSupported?.(m)) || 'audio/webm';
      this.recordedChunks = [];
      this.mediaRecorder = new MediaRecorder(this.combinedStream, { mimeType, audioBitsPerSecond: 64000 });
      this.mediaRecorder.ondataavailable = (e) => { if (e.data && e.data.size > 0) this.recordedChunks.push(e.data); };
      this.mediaRecorder.start(1000); // 1s timeslices
      log('recording started', mimeType);
    } catch (e) {
      log('record start failed', e);
    }
  }

  async _stopRecording() {
    return new Promise((resolve) => {
      if (!this.mediaRecorder || this.mediaRecorder.state === 'inactive') return resolve(null);
      this.mediaRecorder.onstop = () => {
        const blob = new Blob(this.recordedChunks, { type: this.mediaRecorder.mimeType || 'audio/webm' });
        this.recordedChunks = [];
        resolve(blob);
      };
      try { this.mediaRecorder.stop(); } catch { resolve(null); }
    });
  }

  /** Make an outbound call.
   * @returns {Promise<{ok:boolean, error?:string}>}
   */
  async call(targetUri, meta = {}) {
    if (!this.ua || !this.ua.isRegistered()) return { ok: false, error: 'Not registered' };
    if (this.session) return { ok: false, error: 'Already on a call' };

    // Get mic stream BEFORE invite so JsSIP picks it up
    try {
      this.localStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
    } catch (e) {
      return { ok: false, error: 'Microphone permission denied' };
    }

    const eventHandlers = {
      progress: () => this.emit('progress'),
      failed: (e) => {
        log('failed', e.cause);
        this.emit('failed', e);
        this._cleanupAfterCall();
      },
      ended: () => {
        log('ended');
        this.emit('ended');
        this._finalizeCallWithRecording();
      },
      confirmed: () => {
        log('confirmed (answered)');
        this.emit('confirmed');
      },
    };

    const session = this.ua.call(targetUri, {
      eventHandlers,
      mediaStream: this.localStream,
      pcConfig: {
        iceServers: [{ urls: ['stun:stun.l.google.com:19302'] }],
      },
      rtcOfferConstraints: { offerToReceiveAudio: 1, offerToReceiveVideo: 0 },
    });

    this.session = session;
    this.callMeta = meta;
    this._ensureRemoteAudioEl();

    // Wire remote stream
    session.connection.addEventListener('track', (event) => {
      if (!this.remoteStream) this.remoteStream = new MediaStream();
      this.remoteStream.addTrack(event.track);
      this.remoteAudioEl.srcObject = this.remoteStream;
      // (Re)build the combined stream + start recording once we have BOTH streams.
      this._buildCombinedStream();
      this._startRecording();
      this.emit('remoteStreamReady');
    });

    return { ok: true };
  }

  hangup() {
    if (!this.session) return;
    try { this.session.terminate(); } catch { /* noop */ }
  }

  setMuted(mute) {
    if (!this.session) return;
    try { mute ? this.session.mute({ audio: true }) : this.session.unmute({ audio: true }); } catch { /* noop */ }
  }

  async _finalizeCallWithRecording() {
    const blob = await this._stopRecording();
    const meta = this.callMeta;
    this._cleanupAfterCall();
    if (blob && meta?.id) this.emit('recordingReady', { blob, callId: meta.id });
  }

  _cleanupAfterCall() {
    try { this.localStream?.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
    try { this.remoteStream?.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
    try { this.audioContext?.close(); } catch { /* noop */ }
    this.localStream = null;
    this.remoteStream = null;
    this.combinedStream = null;
    this.audioContext = null;
    this.audioMixDestination = null;
    this.session = null;
    this.callMeta = null;
    if (this.remoteAudioEl) this.remoteAudioEl.srcObject = null;
  }

  /** Returns the combined MediaStream used for recording / broadcast. */
  getCombinedStream() { return this.combinedStream; }
  getLocalStream() { return this.localStream; }
  /** Plays an inbound (admin whisper) audio stream locally on the agent side. */
  playWhisperStream(mediaStream) {
    this._ensureWhisperAudioEl();
    this.whisperAudioEl.srcObject = mediaStream;
    this.whisperAudioEl.play?.().catch(() => { /* noop */ });
  }
  stopWhisperStream() {
    if (this.whisperAudioEl) this.whisperAudioEl.srcObject = null;
  }
}

let _instance = null;
export function getWebPhone() {
  if (!_instance) _instance = new WebPhone();
  return _instance;
}
